import keyBy from 'lodash/keyBy';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { updateUser } from 'redux/User';
import { useSelectUser } from 'redux/User/selectors';
import { WellFullDetailsDTO, WellFullDetailsWithUniqueKeyDTO } from 'types/well';

import { useFetchWellsWithWellbores } from './useFetchWellsWithWellbores';
import userApiClient from '../services/networking/userApiClient';

export const useFavouriteWellsManager = () => {
  const user = useSelectUser();
  // raise an error if user is undefined
  if (user === undefined) throw new Error('user is undefined');

  const dispatch = useDispatch();
  const { wells } = useFetchWellsWithWellbores();

  const isFavourite = useCallback(
    (well: WellFullDetailsDTO): boolean => {
      if (!user) return false;
      if (!user.favouriteWells) return false;
      if (!user?.favouriteWells.wellIds && !user?.favouriteWells.tuwis) return false;

      return (well?.wellId && user?.favouriteWells.wellIds.includes(well.wellId)) ||
        (well?.tuwi && user?.favouriteWells.tuwis.includes(well.tuwi))
        ? true
        : false;
    },
    [user],
  );

  const userFavouriteWells = useMemo(() => {
    const favouriteWells = new Set<WellFullDetailsWithUniqueKeyDTO>();

    // if the user is not logged in, then return empty array
    if (!user) return favouriteWells;

    // if Wells is undefined, then return empty array
    if (!wells) return favouriteWells;

    // check if user?.favouriteWells defined, if not, then return empty array
    if (!user?.favouriteWells) {
      return [...favouriteWells];
    }

    const favouriteTuwis = user?.favouriteWells.tuwis ?? [];
    const favouriteWellIds = user?.favouriteWells.wellIds ?? [];

    if (favouriteTuwis.length) {
      const wellsByTuwi = keyBy(wells, 'tuwi');

      for (const tuwi of favouriteTuwis) {
        if (wellsByTuwi[tuwi]) favouriteWells.add(wellsByTuwi[tuwi]);
      }
    }

    if (favouriteWellIds?.length) {
      const wellsByWellId = keyBy(wells, 'wellId');

      for (const wellId of favouriteWellIds) {
        if (wellsByWellId[wellId]) favouriteWells.add(wellsByWellId[wellId]);
      }
    }

    return [...favouriteWells];
  }, [user, wells]);

  const addUserFavouriteWell = useCallback(
    async (addedWell: WellFullDetailsDTO) => {
      if (!user) return;
      if (isFavourite(addedWell)) return;
      const userFavouriteWells = addedWell.wellId
        ? {
            wellIds: [...(user?.favouriteWells.wellIds ?? []), addedWell.wellId],
            tuwis: user?.favouriteWells?.tuwis,
          }
        : addedWell.tuwi
        ? {
            wellIds: user?.favouriteWells.wellIds,
            tuwis: [...(user?.favouriteWells.tuwis ?? []), addedWell.tuwi],
          }
        : user.favouriteWells;
      const newUser = await userApiClient.patchUserFavouriteWells(userFavouriteWells);
      if (newUser) dispatch(updateUser(newUser));
    },
    [dispatch, isFavourite, user],
  );

  const removeUserFavouriteWell = useCallback(
    async (removedWell: WellFullDetailsDTO) => {
      if (!user) return;
      if (!isFavourite(removedWell)) return;
      const userFavouriteWells = {
        wellIds: user?.favouriteWells.wellIds.filter(
          favouriteWellId => favouriteWellId !== removedWell.wellId,
        ),
        tuwis: user?.favouriteWells.tuwis.filter(
          favouriteTuwi => favouriteTuwi !== removedWell.tuwi,
        ),
      };
      const newUser = await userApiClient.patchUserFavouriteWells(userFavouriteWells);
      if (newUser) dispatch(updateUser(newUser));
    },
    [dispatch, isFavourite, user],
  );

  return { isFavourite, addUserFavouriteWell, removeUserFavouriteWell, userFavouriteWells };
};
